import React, { useState, useEffect, useRef } from 'react';
import './NewCourt.css';
import { Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import Select from 'react-select';
import Swal from "sweetalert2";
import { useNavigate, useLocation } from 'react-router-dom';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const NewCourt = () => {
  const { selectedCenter } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const courtData = queryParams.get('courtId');
  const isEditMode = !!courtData;

  const token = localStorage.getItem('auth-token');

  const [timings, setTimings] = useState([{ openTime: '', closeTime: '' }]);
  const [courtName, setCourtName] = useState('');
  const [sport, setSport] = useState('');
  const [maxPlayers, setMaxPlayers] = useState('');
  const [courtType, setCourtType] = useState('');
  const [sportsList, setSportsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const baselineRef = useRef(null);
  const initializedBaselineRef = useRef(false);
  const [editLoaded, setEditLoaded] = useState(false);

  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time24 = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        const label = `${displayHour}:${String(minute).padStart(2, '0')} ${period}`;
        times.push({ value: time24, label });
      }
    }
    return times;
  };

  const compareTimes = (time1, time2) => {
    const [h1, m1] = time1.split(':').map(Number);
    const [h2, m2] = time2.split(':').map(Number);
    if (h1 === h2) return m1 - m2;
    return h1 - h2;
  };

  const timeOptions = generateTimeOptions();

  useEffect(() => {
    if (!selectedCenter) return;

    const fetchSports = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_SPORT_COURT_TYPE}?academyIds=${selectedCenter}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const payload = await res.json();

        if (res.ok && payload.success) {
          const sports = (payload.data_list || []).map(s => ({
            id: s.sportId,
            name: s.sportName,
            courtTypeOptions: Object.entries(s.CourtTypes || {}).map(
              ([typeId, typeName]) => ({ value: Number(typeId), label: typeName })
            )
          }));
          setSportsList(sports);
        } else {
          throw new Error(payload.success_message || 'Error fetching sports');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSports();
  }, [selectedCenter]);

  const courtTypeOptions = sport
    ? (sportsList.find(s => s.id === Number(sport)) || { courtTypeOptions: [] }).courtTypeOptions
    : [];

  useEffect(() => {
    if (!courtData) return;

    const fetchCourtDetails = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_EDIT_COURT}/${courtData}/edit`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${token}`,
          }
        });

        const data = await response.json();
        if (response.ok && data.success) {
          const court = data.data.courtForm;
          setCourtName(court.name);
          setSport(court.sportId?.toString() || '');
          setMaxPlayers(court.maxPlayersAllowed?.toString() || '');
          setCourtType(court.courtTypeId?.toString() || '');
          setTimings(
            (court.courtTimings || []).map(t => ({
              openTime: t.open_time.substring(0, 5),
              closeTime: t.close_time.substring(0, 5)
            }))
          );
          setEditLoaded(true);
        } else {
          throw new Error(data.success_message || 'Error fetching court details');
        }
      } catch (err) {
        setError(err.message);
      }
    };

    fetchCourtDetails();
  }, [courtData]);

  const addTiming = () => {
    setTimings([...timings, { openTime: '', closeTime: '' }]);
  };

  const removeTiming = (index) => {
    if (timings.length > 1) {
      setTimings(timings.filter((_, i) => i !== index));
    } else {
      alert('At least one timing row must be present.');
    }
  };

  const updateTiming = (index, field, value) => {
    const updatedTimings = [...timings];
    updatedTimings[index][field] = value;

    if (field === 'openTime') {
      if (compareTimes(value, updatedTimings[index].closeTime) > 0) {
        updatedTimings[index].closeTime = value;
      }
    } else if (field === 'closeTime') {
      if (compareTimes(value, updatedTimings[index].openTime) < 0) {
        if (value === '00:00' && updatedTimings[index].openTime >= '23:00') {
        } else {
          updatedTimings[index].closeTime = updatedTimings[index].openTime;
        }
      }
    }

    setTimings(updatedTimings);
  };

  useEffect(() => {
    if (!isEditMode) {
      setCourtType('');
    }
  }, [sport, isEditMode]);

  const buildSubmitPayload = () => {
    const formattedTimings = timings
      .filter(({ openTime, closeTime }) => openTime && closeTime)
      .map(({ openTime, closeTime }) => {
        const today = new Date();
        const baseDate = today.toISOString().split('T')[0];
  
        const openDateTime = new Date(`${baseDate}T${openTime}:00.000Z`);
        if (isNaN(openDateTime)) {
          throw new Error(`Invalid open time: ${openTime}`);
        }
  
        let closeDateTime;
        if (closeTime === '00:00') {
          const nextDay = new Date(`${baseDate}T00:00:00.000Z`);
          nextDay.setUTCDate(nextDay.getUTCDate() + 1);
          closeDateTime = nextDay;
        } else {
          closeDateTime = new Date(`${baseDate}T${closeTime}:00.000Z`);
        }
  
        if (isNaN(closeDateTime)) {
          throw new Error(`Invalid close time: ${closeTime}`);
        }
  
        return {
          id: null,
          open_time: openDateTime.toISOString(),
          close_time: closeDateTime.toISOString(),
        };
      });
  
    return {
      id: courtData || null,
      courtTypeId: Number(courtType) || null,
      name: courtName || '',
      academyId: selectedCenter || null,
      sportId: Number(sport) || null,
      maxPlayersAllowed: maxPlayers ? parseInt(maxPlayers, 10) : null,
      courtType: courtType || '',
      courtTimings: formattedTimings,
    };
  };
  

  const redactForAudit = (payload) => {
    if (!payload) return payload;
    const copy = { ...payload };
    if (Array.isArray(copy.courtTimings)) {
      copy.courtTimings = copy.courtTimings.map(t => {
        const hhmm = (iso) => (typeof iso === 'string' && iso.length >= 16) ? iso.substring(11, 16) : iso;
        return { ...t, open_time: hhmm(t.open_time), close_time: hhmm(t.close_time) };
      });
    }
    return copy;
  };

  useEffect(() => {
    if (initializedBaselineRef.current) return;
    const readyForBaseline = isEditMode ? editLoaded : true;
    if (readyForBaseline) {
      baselineRef.current = buildSubmitPayload();
      initializedBaselineRef.current = true;
    }
  }, [isEditMode, editLoaded, courtName, sport, maxPlayers, courtType, timings.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!courtName || !sport || !maxPlayers || !timings.length || !courtType) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Form",
        text: "Please fill out all required fields before submitting.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    const newPayload = buildSubmitPayload();
    const oldPayload = baselineRef.current || {};

    if (isEditMode) {
      const { digest, html } = buildDiffPreview(redactForAudit(oldPayload), redactForAudit(newPayload));
      if (!digest.length) {
        Swal.fire({
          icon: 'info',
          title: 'Nothing to update',
          text: 'No differences detected.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2500,
        });
        return;
      }
      const confirm = await Swal.fire({
        title: 'Apply these changes?',
        html,
        showCancelButton: true,
        confirmButtonText: 'Update',
      });
      if (!confirm.isConfirmed) return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(process.env.REACT_APP_API_CREATE_COURT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newPayload),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        const affected_id =
          courtData ||
          data?.data?.courtId ||
          data?.data?.id ||
          null;

        fireAudit({
          entity: 'court',
          affected_id,
          action: isEditMode ? 'update' : 'create',
          businessApi: process.env.REACT_APP_API_CREATE_COURT,
          businessMethod: 'POST',
          oldPayload: redactForAudit(oldPayload),
          newPayload: redactForAudit(newPayload),
          section: 'Court Booking',
          subSection: 'NewCourt',
          extra: { page: 'NewCourt' },
        });

        baselineRef.current = newPayload;

        navigate("/courtlist");
        setFormSubmitted(true);
        Swal.fire({
          icon: "success",
          title: "Success",
          text: isEditMode ? "Court updated successfully!" : "Court submitted successfully!",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      } else {
        throw new Error(data.success_message || "Error submitting court data");
      }
    } catch (err) {
      setError(err.message);
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: err.message,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="enrollment-container" style={{ overflow: 'visible' }}>
      <div className="breadcrumb">
        <Link to="/courtlist" className="breadcrumb-link">Court List</Link> &gt; {isEditMode ? 'Edit Court' : 'New Court'}
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="text"
            name="courtName"
            className={`walkin-form ${courtName ? "has-value" : ""}`}
            value={courtName}
            onChange={(e) => setCourtName(e.target.value)}
            placeholder="Enter Court Name"
            required
          />
          {courtName && <label htmlFor="courtName">Court Name</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="number"
            name="maxPlayers"
            className={`walkin-form ${maxPlayers ? "has-value" : ""}`}
            value={maxPlayers}
            onChange={(e) => setMaxPlayers(e.target.value)}
            placeholder="Enter Max Players"
            required
            min="1"
            onWheel={e => e.target.blur()}
            onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
          />
          {maxPlayers && <label htmlFor="maxPlayers">Max Players per Slot</label>}
        </div>

        <div className="new-court-form-group" style={{display:'flex',marginTop:'10px'}}>
          <label className="new-court-label">Sport:</label>
          <div className="sport-options">
            {sportsList.map((sportItem) => (
              <label key={sportItem.id} className="radio-label">
                <input
                  type="radio"
                  name="sport"
                  value={sportItem.id}
                  checked={sport === sportItem.id.toString()}
                  onChange={(e) => {
                    setSport(e.target.value);
                    setCourtType('');
                  }}
                  required
                />
                {sportItem.name}
              </label>
            ))}
          </div>
        </div>

        <div className="floating-select" style={{width:'98%'}}>
          <Select
            options={courtTypeOptions}
            value={courtTypeOptions.find(option => option.value === Number(courtType))}
            onChange={(selectedOption) => setCourtType(selectedOption.value)}
            placeholder="Select Court Type"
            className={`walkin-form-dropdown ${courtType ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            isSearchable
            styles={{
              control: (provided) => ({ ...provided, padding: '3px' }),
              placeholder: (provided) => ({ ...provided, color: 'black' }),
            }}
          />
          {courtType && (
            <label style={{ position: 'absolute', top: '-8px', left: '10px', fontSize: '12px', color: '#555', backgroundColor: 'white', padding: '0 5px' }}>
              Court Type
            </label>
          )}
        </div>

        <h3>Timings</h3>
        <table className="new-court-table">
          <thead>
            <tr>
              <th>Open Time</th>
              <th>Close Time</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {timings.map((timing, index) => (
              <tr key={index}>
                <td>
                  <Select
                    value={timeOptions.find(option => option.value === timing.openTime)}
                    onChange={(selectedOption) => updateTiming(index, 'openTime', selectedOption.value)}
                    options={timeOptions}
                    className="new-court-time-select"
                    isSearchable
                    required
                  />
                </td>
                <td>
                  <Select
                    value={timeOptions.find(option => option.value === timing.closeTime)}
                    onChange={(selectedOption) => updateTiming(index, 'closeTime', selectedOption.value)}
                    options={timeOptions}
                    className="new-court-time-select"
                    isSearchable
                    required
                  />
                </td>
                <td>
                  <button
                    type="button"
                    onClick={() => removeTiming(index)}
                    className="new-court-remove-button"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z" />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div>
          <button
            type="button"
            className="plusButton"
            onClick={addTiming}
            title="Add Plan Detail"
          >
            <svg className="plusIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
              <g>
                <path d="M13.75 23.75V16.25H6.25V13.75H13.75V6.25H16.25V13.75H23.75V16.25H16.25V23.75H13.75Z"></path>
              </g>
            </svg>
          </button>
        </div>

        <div className="new-court-submit-container">
          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? (isEditMode ? 'Saving...' : 'Submitting...') : (isEditMode ? 'Save Changes' : 'Submit')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewCourt;
